import React, { useState } from "react";
import { useRouter } from "../util/router";
import Meta from "../components/Meta";
import Section from "../components/Section";
import SectionHeader from "../components/SectionHeader";
import { useForm } from "react-hook-form";
import TextField from "../components/TextField";
import Button from "../components/Button";
import LoadingIcon from "../components/LoadingIcon";
import { apiRequestNoAuth } from "../util/util";
import { Link } from "../util/router";

// T-011: Normalize phone number to E.164 format
function normalizePhone(phone) {
  if (!phone) return "";
  const hasPlus = phone.trim().startsWith("+");
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return hasPlus ? `+${digits}` : digits;
}

function SignupPage() {
  const router = useRouter();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [validatingEmail, setValidatingEmail] = useState(false);
  const [emailValidated, setEmailValidated] = useState(false);
  const [emailError, setEmailError] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({ mode: "onChange" });

  const email = watch("email");
  const emailField = register("email", {
    required: "Email is required",
    pattern: {
      value: /\S+@\S+\.\S+/,
      message: "Invalid email format",
    },
  });

  // Validate email on blur and on submit (not while typing, so one office IP
  // does not burn the existence-check budget). Returns true when usable.
  const validateEmail = async (emailToValidate) => {
    if (!emailToValidate || !/\S+@\S+\.\S+/.test(emailToValidate)) {
      setEmailValidated(false);
      setEmailError("");
      return false;
    }

    setValidatingEmail(true);
    setEmailError("");

    try {
      // Check if user already exists
      const response = await apiRequestNoAuth("email", "POST", {
        action: "checkFirebaseExists",
        data: { email: emailToValidate },
      });

      if (response.exists) {
        setEmailError("You already have an account. Please sign in.");
        setEmailValidated(false);
        return false;
      } else {
        // Check State Farm email
        const sfResponse = await apiRequestNoAuth("email", "POST", {
          action: "checkStateFarm",
          data: { email: emailToValidate },
        });

        if (!sfResponse.success) {
          setEmailError(sfResponse.message || "Cannot use corporate email address");
          setEmailValidated(false);
          return false;
        } else {
          setEmailValidated(true);
          return true;
        }
      }
    } catch (err) {
      console.error("Email validation error:", err);
      setEmailError("Error validating email");
      return false;
    } finally {
      setValidatingEmail(false);
    }
  };

  // Editing the email invalidates any earlier result.
  React.useEffect(() => {
    setEmailValidated(false);
    setEmailError("");
  }, [email]);

  const onSubmit = async (data) => {
    const emailOk = emailValidated || (await validateEmail(data.email));
    if (!emailOk) {
      setError("Please enter a valid email address");
      return;
    }

    setPending(true);
    setError("");

    try {
      const response = await apiRequestNoAuth("free-signup", "POST", {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: normalizePhone(data.phone),
      });

      if (response && response.token) {
        // Redirect to welcome page with token for password setup
        // Preserve next param if present
        const nextParam = router.query.next ? `&next=${encodeURIComponent(router.query.next)}` : '';
        router.push(`/onboarding/welcome?token=${response.token}${nextParam}`);
      } else {
        setError("Signup failed. Please try again.");
      }
    } catch (err) {
      console.error("Signup error:", err);
      if (err.code === "user_exists") {
        setError("An account with this email already exists. Please sign in.");
      } else {
        setError(err.message || "Signup failed. Please try again.");
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <Meta title="Create Account" />
      <Section size="md" bgColor="bg-gray-50">
        <div className="container max-w-md mx-auto">
          <SectionHeader
            title="Create Your Account"
            subtitle="Join Insurance Agency Training to access our tools and resources"
            className="text-center mb-8"
          />

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 bg-white p-6 rounded-lg shadow">
            <div className="grid grid-cols-2 gap-4">
              <TextField
                id="firstName"
                label="First Name"
                placeholder="John"
                error={errors.firstName}
                {...register("firstName", { required: "First name is required" })}
              />
              <TextField
                id="lastName"
                label="Last Name"
                placeholder="Doe"
                error={errors.lastName}
                {...register("lastName", { required: "Last name is required" })}
              />
            </div>

            <div className="relative">
              <TextField
                type="email"
                id="email"
                label="Email"
                placeholder="john@example.com"
                error={errors.email || (emailError && { message: emailError })}
                {...emailField}
                onBlur={(e) => {
                  emailField.onBlur(e);
                  if (!emailValidated && !emailError) validateEmail(e.target.value);
                }}
              />
              {email && /\S+@\S+\.\S+/.test(email) && (
                <div className="absolute right-3 top-9">
                  {validatingEmail && (
                    <span className="text-gray-400 text-sm">Checking...</span>
                  )}
                  {!validatingEmail && emailValidated && !emailError && (
                    <span className="text-green-500">&#10003;</span>
                  )}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 -mt-2">
              Please use a personal email. Corporate emails may not receive our messages.
            </p>

            <TextField
              type="tel"
              id="phone"
              label="Phone"
              placeholder="(555) 123-4567"
              error={errors.phone}
              {...register("phone", {
                required: "Phone is required",
                pattern: {
                  value: /^[\d\s\-\+\(\)\.]+$/,
                  message: "Please enter a valid phone number",
                },
                minLength: {
                  value: 7,
                  message: "Phone number too short",
                },
              })}
            />

            <div className="flex items-start">
              <input
                type="checkbox"
                id="consent"
                className="mt-1 mr-2"
                {...register("consent", { required: "You must agree to continue" })}
              />
              <label htmlFor="consent" className="text-sm text-gray-600">
                I agree to the{" "}
                <Link to="/legal/terms-of-service" className="text-green-700 underline">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/legal/privacy-policy" className="text-green-700 underline">
                  Privacy Policy
                </Link>
              </label>
            </div>
            {errors.consent && (
              <p className="text-sm text-red-600">{errors.consent.message}</p>
            )}

            <Button
              type="submit"
              size="lg"
              disabled={pending || !emailValidated}
              isBlock={true}
            >
              {pending ? <LoadingIcon className="w-6" /> : "Create Account"}
            </Button>

            <p className="text-center text-sm text-gray-600 mt-4">
              Already have an account?{" "}
              <Link
                to={router.query.next ? `/auth/signin?next=${encodeURIComponent(router.query.next)}` : "/auth/signin"}
                className="text-green-700 underline"
              >
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </Section>
    </>
  );
}

export default SignupPage;
